import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import axios from "axios";
import Footer from "./Footer";
import Header from "./header";
import {
  CATEGORY_LABELS,
  formatAccessList,
  formatEntryFee,
  formatQuestDateRange,
  getCompactQuestFields,
  getQuestBrief,
  getQuestImages,
  getQuestStatus,
  parseQuestEventFlow,
} from "../utils/questDisplay";


function Challengedetails() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [quest, setQuest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchQuestById = async (id) => {
    try {
      setLoading(true);
      setError(null);
      const res = await axios.get(`/api/challenges/${id}`);
      if (res.data.success) {
        setQuest(res.data.quest);
      } else {
        setError(res.data.message || "Quest not found");
      }
    } catch (err) {
      console.error("Error fetching quest:", err);
      setError(err?.response?.data?.message || "Quest not found");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    fetchQuestById(id);
  }, [id]);
 


  const containerVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const imageVariants = {
    hidden: { scale: 1.1, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { duration: 1, ease: "easeOut" },
    },
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FBEFD8] flex items-center justify-center">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="w-16 h-16 border-4 border-[#00F0FB] border-t-transparent rounded-full"
        />
      </div>
    );
  }

  if (error && !quest) {
    return (
      <div className="min-h-screen bg-[#FBEFD8] flex items-center justify-center px-4">
        <motion.div
          initial="hidden"
          animate="visible"
          variants={containerVariants}
          className="text-center"
        >
          <h2 className="font-cinzel text-3xl text-[#000] mb-4">
            Content Not Found
          </h2>
          <p className="font-outfit text-[#555] mb-6">
            The content you're looking for doesn't exist or has been removed.
          </p>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => navigate(-1)}
            className="px-6 py-3 bg-gradient-to-r from-[#00F0FB] to-[#00D1E5] text-black font-outfit font-bold rounded-full"
          >
            Go Back
          </motion.button>
        </motion.div>
      </div>
    );
  }

  const status = getQuestStatus(quest || {});
  const fields = getCompactQuestFields(quest?.description, 3);
  const brief = getQuestBrief(quest?.description);
  const eventFlow = parseQuestEventFlow(quest?.description);
  const questImages = getQuestImages(quest);
  const heroImage = questImages[0];
  const supportingImages = questImages.slice(1);
  const hasAsideImages = supportingImages.length > 0;
  const access = formatAccessList(quest?.accessibility);
  const detailFields = [
    { label: "Schedule", value: formatQuestDateRange(quest?.startDate, quest?.endDate) },
    { label: "Entry", value: formatEntryFee(quest?.entryFeeCents, quest?.currency) },
    { label: "Access", value: access.join(", ") },
    ...fields,
  ];

  return (
    <>
      <Header />
      <motion.div
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        className="min-h-screen bg-[#FBEFD8]"
      >
        {/* Header with Back Button */}
        {/* <div className="bg-white/80 backdrop-blur-sm border-b border-[#E4E4E4] sticky top-0 z-50">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <motion.button
              whileHover={{ x: -5 }}
              onClick={() => navigate(QUEST_LISTING_PATH)}
              className="flex items-center gap-2 text-[#555] hover:text-[#000] font-outfit font-semibold transition-colors"
            >
              <ArrowLeft size={20} />
              Back to What's Happening
            </motion.button>
          </div>
        </div> */}

        {/* Hero Section */}
        <div className="relative h-[500px] overflow-hidden">
          <motion.div
            variants={imageVariants}
            className="absolute inset-0 bg-gradient-to-b from-black/50 to-black/70 z-10"
          />
          {heroImage ? (
            <motion.img
              variants={imageVariants}
              src={heroImage}
              alt={quest?.title}
              className="w-full h-full object-cover"
            />
          ) : (
            <div className="h-full w-full bg-[#152123]" />
          )}

          {/* Title Overlay */}
          <div className="absolute inset-0 z-10 flex items-end">
            <div className="max-w-6xl mx-auto px-4 pb-12 w-full">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3, duration: 0.6 }}
              >
                <h1 className="font-cinzel text-4xl md:text-6xl text-white mb-4 leading-tight">
                  {quest?.title}
                </h1>
                <div className="flex flex-wrap gap-2">
                  <span className={`rounded-full border px-3 py-1 font-outfit text-xs font-semibold ${status.className}`}>
                    {status.label}
                  </span>
                  <span className="rounded-full border border-white/30 bg-white/15 px-3 py-1 font-outfit text-xs font-semibold uppercase tracking-[0.14em] text-white">
                    {CATEGORY_LABELS[quest?.category] || "Quest"}
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="max-w-6xl mx-auto px-4 py-12">
       
       
          <div className="mb-8 grid gap-3 md:grid-cols-3">
            {detailFields.slice(0, 6).map((field, index) => (
              <div key={`${field.label}-${index}`} className="rounded-lg border border-[#DCE5E5] bg-white p-5 shadow-sm">
                <p className="font-outfit text-xs font-bold uppercase tracking-[0.16em] text-[#728083]">
                  {field.label}
                </p>
                <p className="mt-2 font-outfit text-base font-semibold text-[#182124]">
                  {field.value}
                </p>
              </div>
            ))}
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="bg-white rounded-lg border border-[#DCE5E5] shadow-sm overflow-hidden"
          >
            <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-[#E4E4E4] pb-5">
              <div className="p-8 pb-0">
                <p className="font-outfit text-xs font-bold uppercase tracking-[0.16em] text-[#008AA0]">
                  Quest brief
                </p>
                <h2 className="mt-2 font-cinzel text-3xl text-[#111718]">About this opportunity</h2>
              </div>
              <div className="p-8 pb-0">
                <button
                  type="button"
                  onClick={() => navigate(`/submissions?challenge=${quest?._id}`)}
                  className="rounded-full bg-gradient-to-r from-[#B6FCFF] to-[#00C3FF] px-6 py-3 font-outfit text-sm font-bold text-black shadow-sm transition hover:scale-[1.02]"
                >
                  Submit Entry
                </button>
              </div>
            </div>
            <div className={`grid gap-8 p-8 ${hasAsideImages ? "lg:grid-cols-[minmax(0,1fr)_360px]" : "lg:grid-cols-1"}`}>
              <div>
                {eventFlow.length > 0 ? (
                  <div className="space-y-5">
                    {eventFlow.map((section, index) => (
                      <section
                        key={`${section.key}-${index}`}
                        className="rounded-xl border border-[#E3EAEB] bg-[#F9FAFB] p-5"
                      >
                        <div className="flex items-center gap-3">
                          <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-[#E7FBFD] font-outfit text-sm font-bold text-[#008AA0]">
                            {index + 1}
                          </span>
                          <h3 className="font-cinzel text-xl text-[#111718]">{section.label}</h3>
                        </div>

                        {section.paragraphs?.length > 0 && (
                          <div className="mt-4 space-y-4">
                            {section.paragraphs.map((paragraph, paragraphIndex) => (
                              <p
                                key={`${section.key}-paragraph-${paragraphIndex}`}
                                className="font-outfit text-sm leading-7 text-[#303B3E]"
                              >
                                {paragraph}
                              </p>
                            ))}
                          </div>
                        )}

                        {section.items?.length > 0 && (
                          <div className="mt-5 grid gap-3">
                            {section.items.map((item, itemIndex) => (
                              <div
                                key={`${section.key}-item-${itemIndex}`}
                                className="flex items-start gap-3 rounded-lg border border-[#DCE5E5] bg-white p-4"
                              >
                                <CheckCircle2 className="mt-0.5 flex-shrink-0 text-[#008AA0]" size={18} />
                                <div>
                                  <p className="font-outfit text-sm font-bold text-[#182124]">{item.title}</p>
                                  <p className="mt-1 font-outfit text-sm leading-6 text-[#536061]">{item.body}</p>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </section>
                    ))}
                  </div>
                ) : (
                  <div className="space-y-5">
                    {(brief.paragraphs.length ? brief.paragraphs : ["Full details will be published soon."]).map((paragraph, index) => (
                      <p key={`${quest?._id}-brief-${index}`} className="font-outfit text-base leading-8 text-[#303B3E]">
                        {paragraph}
                      </p>
                    ))}
                  </div>
                )}

                {!eventFlow.length && brief.fields.length > 0 && (
                  <div className="mt-8 grid gap-4 md:grid-cols-2">
                    {brief.fields.map((field, index) => (
                      <div key={`${field.label}-${index}`} className="rounded-lg border border-[#E3EAEB] bg-[#F9FAFB] p-4">
                        <p className="font-outfit text-[11px] font-bold uppercase tracking-[0.14em] text-[#728083]">
                          {field.label}
                        </p>
                        <p className="mt-2 font-outfit text-sm leading-6 text-[#263133]">
                          {field.value}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {hasAsideImages && (
              <aside className="space-y-4">
                {supportingImages.map((image, index) => (
                    <img
                      key={`${image}-${index}`}
                      src={image}
                      alt={`${quest?.title || "Quest"} supporting ${index + 1}`}
                      className="h-72 w-full rounded-lg border border-[#DCE5E5] object-cover shadow-sm"
                    />
                  ))}
              </aside>
              )}
            </div>
          </motion.div>

        

        </div>
      </motion.div>
      <Footer />
    </>
  );
}

export default Challengedetails;
